import { matrixCholeskySolver, xAbsoluteSum, xBinning, xFindClosestIndex, xNoiseSanPlot, xSubtract, xyInterpolateLinear, } from 'ml-spectra-processing';
import { getDeltaMatrix, updateSystem } from "./utils.js";
/**
 * Fit the baseline drift by iteratively changing weights of sum square error between the fitted baseline and original signals,
 * for further information about the parameters you can get the {@link https://github.com/zmzhang/airPLS/blob/main/airPLS_manuscript.pdf paper of airPLS}.
 * @param x - X axis data useful when control points or zones are submitted.
 * @param y - Original data.
 * @param options - Options object.
 * @returns An object containing the corrected data, baseline, iteration count, and error.
 */
export default function airPLS(x, y, options = {}) {
    const { xWork, yWork, optionsWork, shouldDownsample } = getDownSampleData(x, y, options);
    const { weights, controlPoints } = getControlPoints(xWork, yWork, optionsWork);
    const { maxIterations = 100, lambda = 10, tolerance = 0.001 } = optionsWork;
    let baseline = [];
    let iteration;
    let sumNegDifferences = Number.MAX_SAFE_INTEGER;
    const corrected = Float64Array.from(yWork);
    const stopCriterion = getStopCriterion(yWork, tolerance);
    const { length } = yWork;
    const { lowerTriangularNonZeros, permutationEncodedArray } = getDeltaMatrix(length, lambda);
    let threshold = 1;
    const l = length - 1;
    let prevNegSum = Number.MAX_SAFE_INTEGER;
    for (iteration = 0; iteration < maxIterations && Math.abs(sumNegDifferences) > stopCriterion; iteration++) {
        const [leftHandSide, rightHandSide] = updateSystem(lowerTriangularNonZeros, yWork, weights);
        const cho = matrixCholeskySolver(leftHandSide, length, permutationEncodedArray);
        if (cho === null) {
            throw new Error('Cholesky decomposition failed');
        }
        baseline = cho(rightHandSide);
        sumNegDifferences = applyCorrection(yWork, baseline, corrected);
        if (iteration === 1) {
            const { positive } = xNoiseSanPlot(corrected);
            threshold = positive;
        }
        else {
            const absChange = Math.abs(prevNegSum / sumNegDifferences);
            if (absChange < 1.01 && absChange > 0.99) {
                break;
            }
        }
        prevNegSum = sumNegDifferences + 0;
        for (let i = 1; i < l; i++) {
            const diff = Math.abs(corrected[i]);
            if (controlPoints[i] < 1 && diff > threshold * 2) {
                weights[i] = 0;
            }
            else {
                weights[i] = Math.exp(-((diff / threshold) ** 2));
            }
        }
        weights[0] = 1;
        weights[l] = 1;
    }
    // Interpolate results back to original resolution if downsampling was applied
    let finalBaseline = baseline;
    let finalCorrected = corrected;
    if (shouldDownsample) {
        finalBaseline = xyInterpolateLinear({ x: xWork, y: baseline }, x);
        finalCorrected = xSubtract(y, finalBaseline);
    }
    return {
        corrected: finalCorrected,
        baseline: finalBaseline,
        iteration,
        error: sumNegDifferences,
    };
}
function applyCorrection(y, baseline, corrected) {
    let sumNegDifferences = 0;
    for (let i = 0; i < y.length; i++) {
        const diff = y[i] - baseline[i];
        if (diff < 0)
            sumNegDifferences += diff;
        corrected[i] = diff;
    }
    return sumNegDifferences;
}
function getDownSampleData(x, y, options = {}) {
    const { autoDownsample = false, maxResolution = 5000, controlPoints, } = options;
    const shouldDownsample = autoDownsample && y.length > maxResolution;
    if (!shouldDownsample) {
        return { xWork: x, yWork: y, optionsWork: options, shouldDownsample };
    }
    const binSize = getDownsampleFactor(y.length, maxResolution);
    const xWork = xBinning(x, {
        binSize,
        keepFirstAndLast: true,
    });
    const yWork = xBinning(y, {
        binSize,
        keepFirstAndLast: true,
    });
    let optionsWork = options;
    // Downsample controlPoints if provided, to match downsampled x and y
    if (controlPoints) {
        const downsampledControlPoints = new Int8Array(xWork.length);
        for (let i = 0; i < x.length; i++) {
            if (controlPoints[i] > 0) {
                const closestIndex = xFindClosestIndex(xWork, x[i]);
                downsampledControlPoints[closestIndex] = 1;
            }
        }
        optionsWork = {
            ...options,
            controlPoints: downsampledControlPoints,
        };
    }
    return { xWork, yWork, optionsWork, shouldDownsample };
}
function getStopCriterion(y, tolerance) {
    const sum = xAbsoluteSum(y);
    return tolerance * sum;
}
function getControlPoints(x, y, options = {}) {
    const { length } = x;
    const { controlPoints = Int8Array.from({ length }).fill(0) } = options;
    const { zones = [], weights = Float64Array.from({ length }).fill(1) } = options;
    if (x.length !== y.length) {
        throw new RangeError('Y should match the length with X');
    }
    else if (controlPoints.length !== x.length) {
        throw new RangeError('controlPoints should match the length with X');
    }
    else if (weights.length !== x.length) {
        throw new RangeError('weights should match the length with X');
    }
    for (const range of zones) {
        let indexFrom = xFindClosestIndex(x, range.from);
        let indexTo = xFindClosestIndex(x, range.to);
        if (indexFrom > indexTo)
            [indexFrom, indexTo] = [indexTo, indexFrom];
        for (let i = indexFrom; i < indexTo; i++) {
            controlPoints[i] = 1;
        }
    }
    return {
        weights,
        controlPoints,
    };
}
/**
 * Calculate the downsampling factor to reduce data to target resolution.
 * @param originalLength - Original data length.
 * @param targetResolution - Target number of points.
 * @returns Downsampling factor.
 */
function getDownsampleFactor(originalLength, targetResolution) {
    return Math.max(1, Math.ceil(originalLength / targetResolution));
}
//# sourceMappingURL=index.js.map